// Конфигурация навигации (Sidebar + Drawer + BottomNav).
//
// Состав пунктов приходит из КЛИЕНТСКОГО РЕЕСТРА МОДУЛЕЙ
// (`@/lib/module-registry`): каждый модуль объявляет свои пункты в
// `apps/web/src/modules/<id>.manifest.ts` — иконку, ключ названия, путь,
// счётчик. Этот файл отвечает только за ИНФОРМАЦИОННУЮ АРХИТЕКТУРУ: какие
// пункты первичны, какие уходят в свёрнутую группу «Инструменты», какие
// системные, и что видит гость. Новый модуль появляется в навигации сам —
// правка здесь нужна, только если он должен попасть в ПЕРВИЧНУЮ группу.
//
// Все label-строки берутся через `t(labelKey)` у потребителей (namespace
// `layout`) — см. sidebar.tsx, mobile-drawer.tsx, bottom-nav.tsx.

import type { ModuleId } from "@studyhub/shared/module";
import {
	type Icon,
	IconCoin,
	IconFileText,
	IconFolder,
	IconHelpCircle,
	IconInfoCircle,
	// Маршрут-2 — знак метода ①②③. Обычный `IconRoute` занят: это канонический
	// знак КУРСА (`SECTION_ICONS.course`), и повторять его нельзя — один знак на
	// одну сущность.
	IconRoute2,
	IconSchool,
} from "@tabler/icons-react";
import { moduleNavEntries, WEB_MODULE_REGISTRY } from "@/lib/module-registry";
import type { WebModuleManifest } from "@/modules/_contract";
import { SECTION_ICONS } from "./section-icons";

export interface NavItem {
	/**
	 * Модуль-владелец пункта. `undefined` — пункт модулю не принадлежит
	 * (Главная, публичные страницы): выключить его нельзя, и гейт доступности
	 * к нему неприменим.
	 *
	 * Поле нужно ровно одному потребителю — `use-nav-availability`: без владельца
	 * поверхность не может спросить карту, а спрашивать по URL-строке значит
	 * завести восьмой скрытый реестр «путь → модуль» (§2.8 п.7).
	 */
	moduleId?: ModuleId;
	/**
	 * Пункт ведёт в продажу (тарифы). Показывается только там, где коммерция
	 * видна: в приложении из App Store его нет (guideline 3.1.1). Отбирает
	 * `use-nav-availability` — один фильтр на все меню (сайдбар, шторка, Cmd-K).
	 */
	commerce?: true;
	labelKey: string;
	/** Короткая подпись для тесного BottomNav (если labelKey длинный и переносится). */
	shortLabelKey?: string;
	icon: Icon;
	to: string;
	/**
	 * Дополнительные префиксы путей, при которых пункт считается активным.
	 * Нужно там, где раздел живёт под одним URL, а его detail-страницы — под
	 * другим: «Библиотека» = `/library`, но карточка источника осталась на
	 * `/materials/$materialId`, а деталь папки — на `/collections/$setId`.
	 * Без этого открытая карточка гасит подсветку раздела в сайдбаре.
	 */
	activePrefixes?: string[];
	badge?: number;
	/**
	 * Тон счётчика. `warning` — приоритетный раздел («Обучение»: темы, готовые
	 * к работе). Янтарная плитка `tint-warning` вместо нейтральной. Рисуется
	 * только при badge > 0 — сам бейдж при нуле не показывается.
	 */
	badgeTone?: "warning";
	/** Тип акцент-маркера справа от пункта (`✦` у голосового тьютора в UI-kit). */
	accent?: "sparkle";
	/**
	 * Подпункты сайдбара. Раскрываются, когда активен сам родитель или любой
	 * его ребёнок.
	 *
	 * Иерархия задаётся ЗДЕСЬ, в поверхности, а не в манифесте модуля: поля
	 * вида `nav.group` в манифестах запрещены (CLAUDE.md §Модульная платформа —
	 * манифест IA-агностичен и декларирует способности, а место в меню
	 * выбирает поверхность). Поэтому дети собираются по ключам реестра, а не
	 * объявляются модулем.
	 */
	children?: NavItem[];
}

export interface NavGroup {
	/** Стабильный ID группы (для key в React-list). */
	id: string;
	eyebrowKey?: string;
	/**
	 * Группа сворачиваемая (Collapsible): маленькая eyebrow-подпись + chevron.
	 * По умолчанию раскрыта (сразу видно набор инструментов), можно свернуть.
	 * Требует `eyebrowKey`. Свёрнутый sidebar (icon-only) флаг игнорирует —
	 * там заголовков нет, пункты подряд.
	 */
	collapsible?: boolean;
	items: NavItem[];
}

// ─── Пункты, у которых нет модуля-владельца ─────────────────────────────────
//
// Главная и публичные страницы (о сервисе, тарифы, FAQ) модулями не являются:
// выключить их нельзя, `ModuleId` им не положен (правило принадлежности §4.4).
// Поэтому они объявлены здесь, а не «фиктивным манифестом».

const NAV_HOME: NavItem = { labelKey: "nav.home", icon: SECTION_ICONS.home, to: "/" };

/**
 * «Папки» — подпункт Библиотеки. Синтетический (как `NAV_HOME`): отдельного
 * модуля `folders` нет и заводить его незачем — папки принадлежат `library`,
 * это её же сущность под своим маршрутом.
 */
const NAV_LIBRARY_FOLDERS: NavItem = {
	moduleId: "library",
	labelKey: "nav.folders",
	icon: IconFolder,
	to: "/library/folders",
};

/**
 * «Каталог курсов» — подпункт Курсов. Синтетический по той же причине, что и
 * «Папки»: каталог принадлежит курсам, отдельного модуля-владельца у него нет.
 *
 * Зачем в меню, если кнопка есть в шапке раздела: каталог — единственный вход
 * в готовые программы, и человек, не открывший «Курсы», о нём не узнаёт вовсе.
 * Пункт виден и гостю (`GUEST_NAV`), и в приложении — адрес один и тот же.
 */
const NAV_COURSE_CATALOG: NavItem = {
	moduleId: "course-templates",
	labelKey: "nav.catalog",
	icon: IconSchool,
	to: "/catalog",
};

// ─── Пункты из реестра ──────────────────────────────────────────────────────

/**
 * Первичная навигация: Главная → Календарь → Обучение → Слабые места →
 * Библиотека → Избранное. Календарь над Обучением — планирование перед курсами;
 * Избранное сразу после Библиотеки (тот же контентный контур).
 *
 * Список ключей, а не предикат по `kind`: это продуктовое решение об IA, и
 * оно обязано быть видно одной строкой. Новый модуль сюда сам НЕ попадает —
 * он появляется в «Инструментах» (см. ниже).
 */
const PRIMARY_NAV_KEYS = ["calendar", "courses", "weak-spots", "library"] as const;

/**
 * Подпункты сайдбара: ключ родителя → ключи детей из реестра.
 *
 * «Избранное» переехало из первичной группы в дети Библиотеки (2026-08-14):
 * фактически это её часть — тот же контентный контур, тот же модуль-владелец
 * (`library.manifest.ts`), — и плоским соседом оно этого не показывало.
 * Свободные материалы отдельным подпунктом НЕ выносим: их показывает сам
 * родитель, дубль был бы лишним.
 */
const SIDEBAR_CHILD_KEYS: Record<string, readonly string[]> = { library: ["favorites"] };

/** Системная группа внизу: Прогресс, затем Корзина (30 дней). */
const SYSTEM_NAV_KEYS = ["progress", "trash"] as const;

/** Пункты нижней навигации на mobile (по бокам от центральной кнопки «＋»). */
const BOTTOM_NAV_KEYS = ["courses", "library"] as const;

// `favorites` больше не в PRIMARY_NAV_KEYS, но зарезервирован: иначе он уехал
// бы в «Инструменты» вторым экземпляром — он уже показан как ребёнок Библиотеки.
const RESERVED_KEYS = new Set<string>([
	...PRIMARY_NAV_KEYS,
	...SYSTEM_NAV_KEYS,
	...Object.values(SIDEBAR_CHILD_KEYS).flat(),
]);

/** Группы навигации, собранные поверх произвольного реестра. */
export interface NavSurfaces {
	main: NavItem[];
	tools: NavItem[];
	system: NavItem[];
	bottom: NavItem[];
	all: NavItem[];
}

/**
 * Собрать все группы навигации из реестра.
 *
 * Функция, а не набор констант, ровно по одной причине: контрольный тест
 * прогоняет её на ФИКТИВНОМ реестре и проверяет, что новый модуль появился в
 * «Инструментах» сам. Проверка на боевом реестре доказывала бы только то, что
 * боевой реестр не изменился.
 */
export function buildNavSurfaces(
	registry: readonly WebModuleManifest[] = WEB_MODULE_REGISTRY,
): NavSurfaces {
	// Объекты строятся ОДИН раз и переиспользуются всеми группами.
	//
	// Тождество критично: `all` склеивает первичную группу с нижней навигацией,
	// а `isNavItemActive` сравнивает пункты по `to`. Пересоздай объект в каждой
	// группе — и «Обучение» окажется в списке дважды, что ломает выбор самого
	// специфичного совпадения.
	const entries = moduleNavEntries(registry);
	const byKey = new Map<string, NavItem>(
		entries.map(({ moduleId, entry }) => [
			entry.key,
			{
				moduleId,
				labelKey: entry.labelKey,
				...(entry.shortLabelKey ? { shortLabelKey: entry.shortLabelKey } : {}),
				icon: entry.icon,
				to: entry.to,
				...(entry.activePrefixes ? { activePrefixes: [...entry.activePrefixes] } : {}),
				...(entry.badgeTone ? { badgeTone: entry.badgeTone } : {}),
				...(entry.accent ? { accent: entry.accent } : {}),
			} satisfies NavItem,
		]),
	);

	/** Пункт по ключу. Бросает: отсутствующий ключ = опечатка в IA, не рантайм-случай. */
	const itemOf = (key: string): NavItem => {
		const item = byKey.get(key);
		if (!item) throw new Error(`nav-config: в реестре нет пункта «${key}»`);
		return item;
	};

	// Дети навешиваются на родителя ЗДЕСЬ: реестр про иерархию не знает и знать
	// не должен. `folders` — синтетический пункт (модуля `folders` нет),
	// остальные берутся из реестра по ключу, поэтому выключение модуля гасит и
	// его подпункт вместе с ним.
	//
	// Результат КЭШИРУЕТСЯ: поверхности сравниваются по ссылке (нижняя навигация
	// обязана переиспользовать объекты первичной, а `all` — не содержать
	// дублей по `to`). Без кэша родитель с детьми становился новым объектом, и
	// оба инварианта ломались разом — это ловят гейты `module-registry.test`.
	const augmented = new Map<string, NavItem>();
	const itemWithChildren = (key: string): NavItem => {
		const cached = augmented.get(key);
		if (cached) return cached;
		const base = itemOf(key);
		const extra =
			key === "library" ? [NAV_LIBRARY_FOLDERS] : key === "courses" ? [NAV_COURSE_CATALOG] : [];
		const fromRegistry = (SIDEBAR_CHILD_KEYS[key] ?? []).filter((k) => byKey.has(k)).map(itemOf);
		const children = [...extra, ...fromRegistry];
		const result = children.length > 0 ? { ...base, children } : base;
		augmented.set(key, result);
		return result;
	};

	const main = [NAV_HOME, ...PRIMARY_NAV_KEYS.map(itemWithChildren)];
	// Вторичная группа «Инструменты»: ВСЁ, что реестр даёт сверх первичной и
	// системной групп. Новый модуль с пунктом навигации приезжает сюда
	// автоматически, в конец (дефолтный `order` = 1000), не перетасовывая
	// существующие.
	const tools = entries
		.filter(({ entry }) => !RESERVED_KEYS.has(entry.key))
		.map(({ entry }) => itemOf(entry.key));
	const system = SYSTEM_NAV_KEYS.map(itemOf);
	// Через тот же кэш — иначе объект разойдётся с первичной навигацией.
	const bottom = [NAV_HOME, ...BOTTOM_NAV_KEYS.map(itemWithChildren)];

	// `all` — плоский список ДЛЯ МАТЧИНГА, поэтому подпункты в него разворачиваются.
	// Без этого у `/library/folders` не было собственного пункта в списке, и самым
	// специфичным совпадением оставалась «Библиотека» (префикс `/library`) —
	// подсвечивались разом родитель и ребёнок. Дубли невозможны: дети берутся из
	// того же кэша объектов, `Set` снимает повтор ссылки.
	const withChildren = (items: NavItem[]): NavItem[] =>
		items.flatMap((item) => [item, ...(item.children ?? [])]);

	return {
		main,
		tools,
		system,
		bottom,
		all: [...new Set([...withChildren(main), ...tools, ...system, ...withChildren(bottom)])],
	};
}

const SURFACES = buildNavSurfaces();

export const MAIN_NAV: NavItem[] = SURFACES.main;

/**
 * Вторичная группа «Инструменты» — свёрнута по умолчанию (см.
 * `SIDEBAR_GROUPS`). НЕ удалять и НЕ урезать: это единственный вход в ~10
 * разделов (конспекты, тесты, карточки, записи, эссе, разборы…), которых нет
 * в первичной навигации. Их удаление = регресс достижимости.
 */
export const TOOLS_NAV: NavItem[] = SURFACES.tools;

export const SYSTEM_NAV: NavItem[] = SURFACES.system;

export const SIDEBAR_GROUPS: NavGroup[] = [
	{ id: "main", items: MAIN_NAV },
	{
		id: "tools",
		eyebrowKey: "nav.section.tools",
		collapsible: true,
		items: TOOLS_NAV,
	},
	{ id: "system", items: SYSTEM_NAV },
];

/**
 * Публичный набор пунктов навигации для неавторизованных пользователей.
 * Только страницы, которые работают без сессии: главная, каталог курсов,
 * о сервисе, тарифы, помощь.
 * Модулями они не являются (см. комментарий у `NAV_HOME`).
 *
 * Каталог стоит сразу после главной и ТОЛЬКО здесь: для гостя это главная
 * причина остаться (единственный раздел с реальным содержимым до входа), а в
 * основной навигации своего пункта у него нет — там вход в каталог живёт
 * кнопкой в шапке раздела «Обучение» (`routes/courses/index.tsx`), и второй
 * пункт меню разошёлся бы с ним по подсветке активного.
 *
 * «Метод» стоит сразу после «О сервисе»: это пара — что за сервис и по какому
 * методу он учит. Тот же пункт есть и в шапке (`GUEST_NAV_ITEMS` в
 * `layout/topbar.tsx`) — на desktop меню шапки и сайдбар видны одновременно,
 * но на mobile шапка гостевых пунктов не несёт, и без записи здесь страница
 * `/method` была бы недостижима из меню вовсе.
 */
export const GUEST_NAV: NavItem[] = [
	NAV_HOME,
	{ labelKey: "nav.catalog", icon: IconSchool, to: "/catalog" },
	{ labelKey: "nav.about", icon: IconInfoCircle, to: "/about" },
	{ labelKey: "nav.method", icon: IconRoute2, to: "/method" },
	// Ведёт в продажу: в приложении из App Store пункта нет (3.1.1). До
	// 03.10.2026 его отсюда не убирал никто — шапка фильтровала свою копию, а
	// боковое меню гостя на iPad (десктопная раскладка) показывало «Тарифы».
	{ labelKey: "nav.pricing", icon: IconCoin, to: "/pricing", commerce: true },
	{ labelKey: "nav.help", icon: IconHelpCircle, to: "/faq" },
];

export const GUEST_SIDEBAR_GROUPS: NavGroup[] = [{ id: "guest", items: GUEST_NAV }];

// Закреплённые пункты в Bottom Nav на mobile (по бокам от центральной
// синей кнопки «+»). Центр («Добавить») рендерится в самом bottom-nav.tsx
// и не является NavItem — это action-кнопка, а не маршрут.
// Раскладка (bottom-nav.tsx): [0..1] · «＋» · [2..] · «Ещё».
// «Чат» из нижней навигации убран сознательно — репетитор теперь глобальная
// боковая панель (TutorSidePanel), доступная с любого экрана.
export const BOTTOM_NAV: NavItem[] = SURFACES.bottom;

/** Все пути, по которым пункт может подсветиться (свой + алиасы). */
function prefixesOf(item: NavItem): string[] {
	return item.activePrefixes ? [item.to, ...item.activePrefixes] : [item.to];
}

/** Длина совпавшего префикса или 0, если путь под него не попадает. */
function prefixMatchLength(pathname: string, prefix: string): number {
	if (pathname === prefix) return prefix.length;
	if (prefix === "/") return 0;
	return pathname.startsWith(`${prefix}/`) ? prefix.length : 0;
}

/** Самое специфичное совпадение пункта с текущим путём (0 = не совпал). */
function bestMatchLength(pathname: string, item: NavItem): number {
	return Math.max(...prefixesOf(item).map((p) => prefixMatchLength(pathname, p)));
}

/**
 * Подсветка активного пункта nav. Голый `pathname.startsWith(item.to + '/')`
 * ловит коллизии вложенных путей — например, `/courses/calendar` подсвечивает
 * и «Обучение» (`/courses`), и «Календарь» (`/courses/calendar`). Здесь
 * выбираем пункт с САМЫМ длинным совпадающим префиксом среди известных —
 * короткий родитель гаснет, когда есть более специфичный ребёнок.
 *
 * Учитываются и `activePrefixes` (легаси-адреса и detail-страницы раздела).
 */
export function isNavItemActive(pathname: string, item: NavItem, allItems: NavItem[]): boolean {
	const own = bestMatchLength(pathname, item);
	if (own === 0) return false;
	const longer = allItems.some(
		(other) => other.to !== item.to && bestMatchLength(pathname, other) > own,
	);
	return !longer;
}

/**
 * Плоский список всех nav-пунктов — для collision-aware матчинга.
 * Критично, что здесь есть И `/courses`, И `/courses/calendar`: иначе на
 * `/courses/calendar` подсвечивались бы оба пункта сразу («Обучение» как
 * префикс). Деталь курса `/courses/$id` и тема `/courses/$id/topics/$topicId`
 * своих пунктов не имеют — они осознанно подсвечивают «Обучение».
 *
 * Подпункты (Библиотека → Папки, Избранное) здесь тоже есть — иначе на
 * `/library/folders` побеждал бы префикс родителя и подсвечивались бы оба.
 *
 * Дублей нет: `BOTTOM_NAV` переиспользует ТЕ ЖЕ объекты, что и `MAIN_NAV`
 * (см. `buildNavSurfaces`), а `Set` снимает повтор ссылки.
 */
export const ALL_NAV_ITEMS: NavItem[] = SURFACES.all;

// На случай если потребуется fallback-логотип. Используется в Sidebar (expanded).
export const BRAND = {
	full: "StudyHub",
	short: "S",
	icon: IconFileText,
} as const;

/**
 * Первый ли это уровень навигации — то есть пункт, до которого можно дойти
 * из меню (нижнего, бокового, бургера), а не экран внутри раздела.
 *
 * Нужен ровно для одного решения: между пунктами меню экран НЕ едет, он
 * подменяется мгновенно, а движение остаётся для углубления (открыть курс,
 * документ, конспект). Решение владельца 2026-08-27 после проверки на
 * устройстве: анимация на переключении раздела читается как задержка.
 *
 * Считать по числу сегментов пути нельзя: `/library/folders` и
 * `/courses/calendar` — тоже пункты меню, а `/materials/abc` с тем же числом
 * сегментов — уже углубление. Поэтому источник истины здесь один — реестр.
 */
export function isFirstLevelPath(pathname: string): boolean {
	const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
	return ALL_NAV_ITEMS.some((item) => item.to === normalized);
}
